import { InputProps, Stack, ThemeableStackProps, XStack } from 'tamagui'
import { useId, useState } from 'react'
import { FormFieldContainer } from './form-field-container'
import { FormContainerBaseTypes } from './form-container-types'
import { Pressable } from 'react-native'
import { EyeRegular, EyeSlashRegular, IconProps } from '@tamagui-extras/core'
import { StyledInput, StyledTextArea } from './styled-input'
import { Lock } from '@tamagui/lucide-icons'
import { shadowStandard } from '../../styles'
import { colors } from '../../colors'
export type InputFieldProps = InputProps &
  FormContainerBaseTypes & {
    containerProps?: ThemeableStackProps
    multiline?: boolean
    isPassword?: boolean
    fullWidth?: boolean
    passwordIconProps?: IconProps
    showHelperText?: boolean
  }

export function InputField({
  required,
  error,
  helperText,
  helperTextProps,
  label,
  labelProps,
  labelInline,
  multiline,
  containerProps,
  showRequired,
  hideLabel,
  isPassword,
  passwordIconProps,
  fullWidth,
  isDirty,
  showHelperText,
  editable = true,
  ...rest
}: InputFieldProps) {
  const genId = useId()
  const [show, setShow] = useState<boolean>(false)
  const id = rest.id || genId
  const currentInputProps = {
    ...rest,
  }

  if (fullWidth) {
    currentInputProps.minWidth = '100%'
  }

  let secureTextEntry = !!(rest.secureTextEntry || isPassword)

  const placeholderTextColor = rest.placeholderTextColor || '#616161'

  return (
    <FormFieldContainer
      id={id}
      error={error}
      required={required}
      labelProps={labelProps}
      label={label}
      fullWidth={fullWidth}
      size={rest.size}
      labelInline={labelInline}
      helperText={helperText}
      helperTextProps={helperTextProps}
      hideLabel={hideLabel}
      isDirty={isDirty}
      showRequired={showRequired}
      showHelperText={showHelperText}
      {...containerProps}
    >
      {multiline ? (
        <StyledTextArea
          borderColor={error ? '$red11' : '$white'}
          borderWidth={1}
          placeholderTextColor={placeholderTextColor}
          {...shadowStandard}
          {...currentInputProps}
        />
      ) : secureTextEntry ? (
        <Stack
          {...shadowStandard}
          position={'relative'}
          width={fullWidth ? '100%' : currentInputProps?.width}
        >
          <StyledInput
            secureTextEntry={!show}
            autoCapitalize="none"
            placeholderTextColor={placeholderTextColor}
            editable={editable}
            borderColor={error ? 'red' : 'white'}
            borderWidth={1}
            {...currentInputProps}
          />
          <Pressable
            style={{
              position: 'absolute',
              top: '50%',
              transform: [{ translateY: -0.5 * 20 }],
              height: 20,
              ...(rest?.direction === 'rtl'
                ? {
                    left: 15,
                  }
                : {
                    right: 20,
                  }),
            }}
            onPress={() => {
              setShow((state) => !state)
            }}
          >
            {show ? (
              <EyeSlashRegular color={colors.overlay11} {...passwordIconProps} />
            ) : (
              <EyeRegular color={colors.overlay11} {...passwordIconProps} />
            )}
          </Pressable>
        </Stack>
      ) : (
        <>
          <StyledInput
            position="relative"
            placeholderTextColor={placeholderTextColor}
            autoCapitalize="none"
            editable={editable}
            borderWidth={1}
            {...shadowStandard}
            {...currentInputProps}
            {...(error && { boc: 'red' })}
          />
          {!editable && (
            <XStack position="absolute" right="$2" top="$2.5">
              <Lock />
            </XStack>
          )}
        </>
      )}
    </FormFieldContainer>
  )
}
